/* eslint-disable react/no-array-index-key */
import React, { useEffect, useState } from 'react';

import {
  Grid,
  Table,
  TableBody,
  TableContainer,
  TableRow,
  tableCellClasses,
} from '@mui/material';
import { DateTime } from 'luxon';

import { IRenderedSTOItem, ISTOSuratPesananMappedData } from './interfaces';
import S from './STOSuratPesananOOTPrekursorPrintTemplate.styles';

interface STOSuratPesananOOTPrekursorPrintTemplateComponentProps {
  data: ISTOSuratPesananMappedData;
}

interface InfoRowProps {
  label: string;
  value: React.ReactNode;
  labelWidth?: number;
}

const InfoRow = ({
  label,
  value,
  labelWidth = 3,
}: InfoRowProps): React.JSX.Element => (
  <>
    <Grid item xs={labelWidth}>
      {label}
    </Grid>
    <Grid item xs={12 - labelWidth}>
      : {value}
    </Grid>
  </>
);

const getMaxPageNumber = (tableData: IRenderedSTOItem[][]): number => {
  const additionalPage =
    (tableData.length > 1 && tableData[tableData.length - 1].length > 17) ||
    (tableData.length === 1 && tableData[tableData.length - 1].length > 9)
      ? 1
      : 0;

  return tableData.length + additionalPage;
};

export const STOSuratPesananOOTPrekursorPrintTemplateComponent = ({
  data,
}: STOSuratPesananOOTPrekursorPrintTemplateComponentProps): React.JSX.Element => {
  const [maxPage, setMaxPage] = useState<number>(0);
  const title = data.is_prekursor
    ? 'SURAT PESANAN OBAT MENGANDUNG PREKURSOR FARMASI'
    : data.is_oot
    ? 'SURAT PESANAN OBAT-OBAT TERTENTU'
    : 'SURAT PESANAN';
  const productLabel = data.is_prekursor
    ? 'Obat Jadi Prekursor Farmasi'
    : data.is_oot
    ? 'Obat-Obat Tertentu'
    : 'Obat';

  const renderRepeatingHeaderContent = (): React.JSX.Element => (
    <>
      <S.HeaderCopeWrapper>
        <S.SuratPesananLabel>{title}</S.SuratPesananLabel>
      </S.HeaderCopeWrapper>
      {/* Surat Pesanan Cope Section */}
      <S.STOSuratPesananCopeWrapper container>
        <Grid item xs={2}>
          <img
            src="/assets/print-template/TMPLogo.png"
            alt="logo"
            style={{
              width: '120%',
              marginTop: '12px',
              marginLeft: '-40px',
            }}
          />
        </Grid>
        {/* Section Company Profile */}
        <S.CompanyProfileWrapper item xs={10} style={{ marginLeft: -50 }}>
          <div>
            <b>{data.to_warehouse_info?.name}</b>
          </div>
          <Grid container>
            <InfoRow
              label="Alamat"
              labelWidth={2.5}
              value={data.to_warehouse_info?.address}
            />
            <InfoRow
              label="Alamat NPWP"
              labelWidth={2.5}
              value={data.to_warehouse_info?.npwp_address}
            />
            <InfoRow
              label="No Izin PBF"
              labelWidth={2.5}
              value={data.to_warehouse_info?.pbf_no}
            />
            <InfoRow
              label="No Izin DAK"
              labelWidth={2.5}
              value={data.to_warehouse_info?.dak_no}
            />
            <InfoRow
              label="No Sertifikat CDOB"
              labelWidth={2.5}
              value={data.to_warehouse_info?.cdob_no}
            />
            <InfoRow
              label="No Sertifikat CDAKB"
              labelWidth={2.5}
              value={data.to_warehouse_info?.cdakb_no}
            />
            {data.to_warehouse_info?.cdob_ccp_no && (
              <InfoRow
                label="No Sertifikat CDOB CCP"
                labelWidth={2.5}
                value={data.to_warehouse_info?.cdob_ccp_no}
              />
            )}
            <InfoRow
              label="NPWP"
              labelWidth={2.5}
              value={data.to_warehouse_info?.npwp}
            />
            <InfoRow
              label="No Telp"
              labelWidth={2.5}
              value={data.to_warehouse_info?.phone}
            />
          </Grid>
        </S.CompanyProfileWrapper>
      </S.STOSuratPesananCopeWrapper>
    </>
  );

  const renderHeader = () => (
    <S.PageHeader>
      {renderRepeatingHeaderContent()}

      {/* Nomor PO / Tanggal PO */}
      <S.STOSuratPesananCopeWrapper container>
        <InfoRow label="Nomor TO" value={data.sto_number} />
        <InfoRow label="Tanggal TO" value={data.createdAt} />
      </S.STOSuratPesananCopeWrapper>

      {/* Signatory Section */}
      <S.STOSuratPesananCopeWrapper container>
        <Grid item xs={12}>
          Yang bertanda tangan di bawah ini :
        </Grid>
        <InfoRow label="Nama" value={data.to_warehouse_info?.apj_name} />
        <InfoRow label="Jabatan" value={data.to_warehouse_info?.apj_title} />
        <InfoRow label="No. STRA" value={data.to_warehouse_info?.apj_sipa} />
      </S.STOSuratPesananCopeWrapper>

      {/* Distributor Section */}
      <S.STOSuratPesananCopeWrapper container>
        <Grid item xs={12}>
          Mengajukan pesanan {productLabel} kepada:
        </Grid>
        <InfoRow
          label="Nama Distributor"
          value={data.from_warehouse_info?.name}
        />
        <InfoRow label="Alamat" value={data.from_warehouse_info?.address} />
        <InfoRow label="Telp" value={data.from_warehouse_info?.phone} />
      </S.STOSuratPesananCopeWrapper>

      <S.PrintInfoWrapper container>
        <Grid item xs={12}>
          dengan {productLabel} yang dipesan adalah:
        </Grid>
      </S.PrintInfoWrapper>
    </S.PageHeader>
  );

  useEffect(() => {
    setMaxPage(getMaxPageNumber(data.items));
  }, [maxPage, data.items]);

  // Footer Section
  const renderFooter = () =>
    Array.from(Array(maxPage).keys()).map((idx) => (
      <S.PageFooter key={idx} className="last-page" pageOffset={idx}>
        <Grid container>
          <Grid item xs={10} />
          <Grid item xs={2} style={{ textAlign: 'right' }}>
            <S.PageNumberText>
              Page {idx + 1} of {maxPage}
            </S.PageNumberText>
          </Grid>
        </Grid>
      </S.PageFooter>
    ));

  const renderBody = (
    idx: number,
    itemData: IRenderedSTOItem[],
    isLastPage: boolean,
  ) => {
    const startNumber = data.items
      .slice(0, idx)
      .reduce((total, chunk) => total + chunk.length, 0);

    return (
      <S.PageBody>
        {idx > 0 && maxPage > 1 && (
          <S.RepeatingHeaderWrapper>
            {renderRepeatingHeaderContent()}
          </S.RepeatingHeaderWrapper>
        )}
        <TableContainer style={{ marginTop: idx === 0 ? '0' : '8mm' }}>
          <Table
            sx={{
              minWidth: 650,
              [`& .${tableCellClasses.root}`]: {
                borderBottom: '1px solid black',
                fontFamily: 'Arial',
                fontSize: 11,
              },
            }}
            size="small"
            aria-label="a dense table"
          >
            <S.TableHeadStyle>
              <TableRow>
                <S.TableHeaderCell width={30}>No</S.TableHeaderCell>
                <S.TableHeaderCell width={150}>Nama Produk</S.TableHeaderCell>
                <S.TableHeaderCell width={140}>
                  Zat Aktif/Kekuatan
                </S.TableHeaderCell>
                <S.TableHeaderCell width={70}>Bentuk Sediaan</S.TableHeaderCell>
                <S.TableHeaderCell width={55}>Jumlah (Angka)</S.TableHeaderCell>
                <S.TableHeaderCell width={120}>
                  Jumlah (Terbilang)
                </S.TableHeaderCell>
                <S.TableHeaderCell width={130}>Kemasan</S.TableHeaderCell>
              </TableRow>
            </S.TableHeadStyle>
            <TableBody>
              {itemData.map((productInfo, rowIdx) => (
                <S.TableRowStyle
                  key={`STOSuratPesananOOTPrekursorRow-${productInfo.sku_code}-${rowIdx}`}
                >
                  <S.TableBodyCell>{startNumber + rowIdx + 1}</S.TableBodyCell>
                  <S.TableBodyCell>{productInfo.name}</S.TableBodyCell>
                  <S.TableBodyCell>{productInfo.core_molecule}</S.TableBodyCell>
                  <S.TableBodyCell>{productInfo.dosage_form}</S.TableBodyCell>
                  <S.TableBodyCell>{productInfo.quantity}</S.TableBodyCell>
                  <S.TableBodyCell>
                    {productInfo.quantity_in_words}
                  </S.TableBodyCell>
                  <S.TableBodyCell>{productInfo.uom}</S.TableBodyCell>
                </S.TableRowStyle>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {/* If not last page  */}
        {!isLastPage ? (
          <>
            <div className="pagebreak-after" />
            <div style={{ width: '100%' }} />
          </>
        ) : (
          <>
            {/* Additional page break if maxPage > data.items.length
             because rest of page will not enough to render summary section. Signature section will
             be rendered separately in the next page */}
            {maxPage > data.items.length && (
              <>
                <div className="pagebreak-after" />
                <div style={{ width: '100%' }} />
                {maxPage > 1 && (
                  <S.RepeatingHeaderWrapper>
                    {renderRepeatingHeaderContent()}
                  </S.RepeatingHeaderWrapper>
                )}
              </>
            )}
            {/* Usage Section */}
            <S.PostTableInformationSectionWrapper
              container
              style={{ marginTop: '8px' }}
            >
              <Grid item xs={12}>
                {productLabel} tersebut akan dipergunakan untuk:
              </Grid>
              <InfoRow
                label="Nama Sarana"
                value={data.to_warehouse_info?.name}
              />
              <InfoRow
                label="Alamat Sarana"
                value={data.to_warehouse_info?.address}
              />
            </S.PostTableInformationSectionWrapper>
            {/* Signature Section */}
            <Grid container justifyContent="flex-end">
              <S.SignatureSectionWrapper item xs={5}>
                <Grid container>
                  <Grid item xs={12}>
                    {data.to_warehouse_info?.city},{' '}
                    {DateTime.fromJSDate(new Date()).toFormat('d MMMM yyyy')}
                  </Grid>
                  <Grid item xs={12}>
                    <img
                      id="inv-sign"
                      style={{ width: 120 }}
                      src={data.to_warehouse_info?.apj_signature}
                      alt="ttd"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    {data.to_warehouse_info?.apj_name}
                  </Grid>
                  <Grid item xs={12}>
                    No. STRA: {data.to_warehouse_info?.apj_sipa}
                  </Grid>
                </Grid>
              </S.SignatureSectionWrapper>
            </Grid>
          </>
        )}
      </S.PageBody>
    );
  };

  return (
    <div>
      <S.PageWrapper>
        <TableContainer>
          <Table>
            {/* Header Section */}
            {renderHeader()}

            {/* Content Section */}
            {data.items.map((itemData, idx) =>
              renderBody(idx, itemData, data.items.length === idx + 1),
            )}

            {/* Footer Section */}
            {renderFooter()}
          </Table>
        </TableContainer>
      </S.PageWrapper>
    </div>
  );
};
